<template>
    <div>
        <div class="content-header row">
            <div class="content-header-left col-md-6 col-12 mb-2">
                <h3 class="content-header-title mb-0">B2C Giftcards</h3>
                <div class="row breadcrumbs-top">
                    <div class="breadcrumb-wrapper col-12">
                        <ol class="breadcrumb">
                            <li class="breadcrumb-item"><a href="/">Home</a></li>
                            <li class="breadcrumb-item active">B2C Giftcards</li>
                        </ol>
                    </div>
                </div>
            </div>
        </div>

        <div class="content-body">
            <section class="card">
                <div class="card-header">
                    <h4>B2C Giftcards</h4>
                </div>

                <div class="card-body">
                    <form>
                        <div class="row">
                            <div class="col-md-4">
                                <div class="form-group">
                                    <label for="giftcard-search">Search by code, sender, or recipient</label>
                                    <input
                                        id="giftcard-search"
                                        v-model="search"
                                        class="form-control form-control-sm"
                                        type="text"
                                        placeholder="Code, name, or email"
                                    >
                                </div>
                            </div>
                            <div class="col-md-12 mb-2">
                                <button type="submit" class="btn btn-primary glow mb-1 mb-sm-0" @click.prevent="applySearch">
                                    Search
                                </button>
                                <button type="reset" class="btn btn-warning glow mb-1 mb-sm-0" @click.prevent="resetFilters">
                                    Reset
                                </button>
                            </div>
                        </div>
                    </form>

                    <vue-good-table
                        class="dataTable"
                        mode="remote"
                        :isLoading.sync="tableDataLoading"
                        :totalRows="totalRecords"
                        :pagination-options="paginationOptions"
                        :sort-options="sortingOptions"
                        @on-page-change="this.onPageChange"
                        @on-per-page-change="this.onPerPageChange"
                        styleClass="vgt-table"
                        :columns="columns"
                        :rows="allGiftcards"
                    >
                        <template slot="table-row" slot-scope="props">
                            <span v-if="props.column.field === 'sn'">
                                {{ serialNo(props) }}
                            </span>
                            <span v-else-if="props.column.field === 'is_redeemed'">
                                <span v-if="props.row.is_redeemed" class="badge badge-glow badge-success">Redeemed</span>
                                <span v-else class="badge badge-glow badge-danger">Not Redeemed</span>
                            </span>
                            <span v-else-if="props.column.field === 'recipient_email'">
                                {{ props.row.recipient_name }} ({{ props.row.recipient_email }})
                            </span>
                            <span v-else-if="props.column.field === 'sender_id'">
                                <template v-if="props.row.sender">
                                    {{ props.row.sender.first_name }} {{ props.row.sender.last_name }} ({{ props.row.sender.email }})
                                </template>
                                <template v-else>
                                    {{ props.row.sender_email || '—' }}
                                </template>
                            </span>
                            <span v-else-if="props.column.field === 'code'">
                                <span class="text-bold-700">{{ props.row.code }}</span>
                            </span>
                            <span v-else-if="props.column.field === 'created_at'">
                                {{ formatDateTime(props.row.created_at) }}
                            </span>
                            <table-row-actions v-else-if="props.column.field === 'action'">
                                <Link
                                    v-if="!props.row.is_redeemed"
                                    :href="route('admin.giftcards.edit', props.row.id)"
                                    class="dropdown-item"
                                >
                                    <i class="feather icon-edit"></i>
                                    Edit
                                </Link>
                                <a
                                    href="#"
                                    class="dropdown-item text-danger"
                                    :class="{ unclickable: props.row.is_redeemed }"
                                    @click.prevent="showAlert(props.row.id)"
                                >
                                    <i class="feather icon-trash-2"></i>
                                    Delete
                                </a>
                            </table-row-actions>
                        </template>
                    </vue-good-table>
                </div>
            </section>
        </div>
    </div>
</template>

<script>
import Layout from '@/Shared/Layout';
import { Link } from '@inertiajs/vue2';
import DeleteAlert from '../../Shared/Mixins/DeleteAlert';
import PaginationMixin from '../../Shared/Mixins/PaginationMixin';
import { formatDateTime } from '../../utils';

export default {
    metaInfo: { title: 'B2C Giftcards' },
    props: ['giftcards'],
    layout: Layout,
    name: 'GiftcardIndex',
    components: {
        Link,
    },
    mixins: [
        DeleteAlert,
        PaginationMixin,
    ],
    data() {
        return {
            search: '',
            allGiftcards: [],
            totalRecords: 0,
            serverParams: {
                page: 1,
                perPage: 10,
            },
            paginationOptions: {
                enabled: true,
                mode: 'pages',
                perPage: 10,
                perPageDropdown: [5, 10, 20, 50, 100, 200],
                dropdownAllowAll: false,
            },
            sortingOptions: {
                enabled: false,
            },
            columns: [
                { label: 'S/N', field: 'sn', thClass: 'text-center', tdClass: 'text-center' },
                { label: 'Code', field: 'code', thClass: 'text-center', tdClass: 'text-center' },
                { label: 'Sender', field: 'sender_id', thClass: 'text-center', tdClass: 'text-center' },
                { label: 'Recipient', field: 'recipient_email', thClass: 'text-center', tdClass: 'text-center' },
                {
                    label: 'Amount',
                    field: 'amount',
                    thClass: 'text-center',
                    tdClass: 'text-center',
                    formatFn: this.formatFn,
                },
                { label: 'Status', field: 'is_redeemed', thClass: 'text-center', tdClass: 'text-center' },
                {
                    label: 'Created At',
                    field: 'created_at',
                    thClass: 'text-center',
                    tdClass: 'text-center',
                },
                { label: 'Action', field: 'action', sortable: false },
            ],
            tableDataLoading: false,
        };
    },
    mounted() {
        this.allGiftcards = this.giftcards?.data ?? [];
        this.totalRecords = this.giftcards?.total ?? 0;
    },
    methods: {
        formatDateTime,
        formatFn(value) {
            return '₦' + Number(value ?? 0).toLocaleString();
        },
        applySearch() {
            this.updateParams({ page: 1 });
            this.paginatedData();
        },
        resetFilters() {
            this.search = '';
            this.updateParams({ page: 1 });
            this.paginatedData();
        },
        async paginatedData() {
            try {
                this.tableDataLoading = true;

                const response = await axios.get(route('admin.giftcards.index'), {
                    params: {
                        page: this.serverParams.page,
                        per_page: this.serverParams.perPage,
                        search: this.search || undefined,
                        api: true,
                    },
                    headers: {
                        'X-Requested-With': 'XMLHttpRequest',
                    },
                });

                this.totalRecords = response.data.total;
                this.paginationOptions.perPage = response.data.per_page;
                this.serverParams.perPage = response.data.per_page;
                this.paginationOptions.setCurrentPage = response.data.current_page;
                this.allGiftcards = response.data.data;
            } catch (e) {
                console.log(e);
            } finally {
                this.tableDataLoading = false;
            }
        },
        async deleteGiftcard(id) {
            await this.$inertia.delete(route('admin.giftcards.delete', id), {
                preserveScroll: true,
                onSuccess: async () => {
                    await this.paginatedData();
                },
            });
        },
        async showAlert(giftcardId) {
            const details = {
                title: 'Are you sure you want to delete this giftcard?',
                text: "You won't be able to revert this!",
            };

            if (await this.deleteAlert(details.title, details.text)) {
                await this.deleteGiftcard(giftcardId);
            }
        },
    },
};
</script>

<style scoped>
.unclickable {
    pointer-events: none;
    color: grey;
    text-decoration: none;
}
</style>
